import { ArrowBack, Security } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import CustomerIntakePublicShell from '../components/CustomerIntake/CustomerIntakePublicShell';

type PolicySectionProps = {
  id: string;
  title: string;
  children: ReactNode;
};

const PolicySection = ({ id, title, children }: PolicySectionProps) => (
  <Box component="section" id={id} className="customer-intake-policy-section">
    <Typography
      component="h2"
      variant="h6"
      color="secondary.main"
      fontWeight={700}
    >
      {title}
    </Typography>
    <Box sx={{ mt: 1.25 }}>{children}</Box>
  </Box>
);

const CustomerIntakePrivacy = () => {
  return (
    <CustomerIntakePublicShell className="customer-intake-policy-page">
      <Container maxWidth="md" className="customer-intake-page-container">
        <Box className="customer-intake-hero">
          <Typography className="customer-intake-eyebrow">
            Aquatics HUB
          </Typography>
          <Typography
            component="h1"
            variant="h3"
            className="customer-intake-title"
            sx={{ mt: 1 }}
          >
            Personal Data Protection Policy
          </Typography>
          <Typography variant="body1" className="customer-intake-subtitle">
            Please review how Aquatics HUB uses and protects the information
            provided through this customer registration form.
          </Typography>
        </Box>

        <Card className="customer-intake-card customer-intake-policy-card">
          <Stack spacing={2.5}>
            <Alert severity="info" icon={<Security />}>
              This page is a frontend UI prototype. The final policy wording
              must be reviewed and approved before production use.
            </Alert>

            <Typography variant="body2" color="text.secondary">
              Version: Draft for review
            </Typography>

            <Divider />

            <PolicySection
              id="information-collected"
              title="1. Information we collect"
            >
              <Typography variant="body1" color="text.secondary">
                We may collect the information you choose to provide through
                this form, such as your name, phone number, email address,
                customer type, date of birth, preferences, and swimming needs.
              </Typography>
            </PolicySection>

            <PolicySection id="purpose" title="2. How we use your information">
              <Typography variant="body1" color="text.secondary">
                Aquatics HUB may use the submitted information to understand
                your needs, recommend suitable swimming services, arrange a
                consultation, and support your registration journey.
              </Typography>
            </PolicySection>

            <PolicySection id="consent" title="3. Consent and choices">
              <Typography variant="body1" color="text.secondary">
                You may choose which optional information to provide. Required
                fields are identified in the form. By submitting the form, you
                confirm that the information you provide is accurate to the best
                of your knowledge.
              </Typography>
            </PolicySection>

            <PolicySection id="sharing" title="4. Sharing of information">
              <Typography variant="body1" color="text.secondary">
                Information should only be accessed by authorized Aquatics HUB
                staff or service providers who need it to support the purposes
                described in this policy. We do not sell personal information.
              </Typography>
            </PolicySection>

            <PolicySection id="retention" title="5. Storage and retention">
              <Typography variant="body1" color="text.secondary">
                Information should be kept only for as long as it is needed for
                the stated business purpose or as required by applicable rules.
                The final retention period is subject to business and legal
                approval.
              </Typography>
            </PolicySection>

            <PolicySection id="rights" title="6. Your rights">
              <Typography variant="body1" color="text.secondary">
                Subject to applicable law, you may have the right to request
                access to, correction of, or deletion of your personal
                information, or to withdraw consent for optional contact.
              </Typography>
            </PolicySection>

            <PolicySection id="security" title="7. Data security">
              <Typography variant="body1" color="text.secondary">
                Aquatics HUB should apply reasonable administrative, technical,
                and organizational safeguards to protect personal information
                against unauthorized access, loss, misuse, or disclosure.
              </Typography>
            </PolicySection>

            <PolicySection id="contact" title="8. Contact us">
              <Typography variant="body1" color="text.secondary">
                For questions about this policy or the information you provide,
                please contact the Aquatics HUB reception team.
              </Typography>
              <List
                dense
                disablePadding
                className="customer-intake-policy-list"
              >
                <ListItem disableGutters>
                  <ListItemIcon>
                    <Security color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Aquatics HUB reception"
                    secondary="Contact details will be added after business approval."
                  />
                </ListItem>
              </List>
            </PolicySection>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                component={RouterLink}
                to="/customer-survey/form"
                variant="contained"
                startIcon={<ArrowBack />}
              >
                Back to customer form
              </Button>
              <Button
                component={RouterLink}
                to="/customer-survey/qr"
                variant="outlined"
              >
                View QR page
              </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary">
              This draft is for interface review only. See the{' '}
              <Link component={RouterLink} to="/customer-survey/form">
                customer information form
              </Link>{' '}
              to return.
            </Typography>
          </Stack>
        </Card>
      </Container>
    </CustomerIntakePublicShell>
  );
};

export default CustomerIntakePrivacy;
